<template>
  <div class="searchWrapper">
    <div class="pageTop">
      <div class="d-none d-md-block">
        <div class="d-flex flex-column">
          <b-breadcrumb :items="crumb_links"></b-breadcrumb>
          <div class="text-left ml-2 d-flex">
            <h4 style="color: #930">TAIR Gene Search</h4>
            <span class="ml-2"
              >[<a :href="helpLink" target="_blank">Help</a>]</span
            >
          </div>
          <div class="text-left m-2">
            Genes may be searched by name, keywords, features, and/or location.
            In TAIR, a Gene Model is defined as any description of a gene
            product from a variety of sources including computational
            prediction, mRNA sequencing, or genetic characterization. A locus is
            defined as the genomic sequence corresponding to a transcribed unit
            (e.g. AT2G03340) in the genome. In TAIR, many gene models can exist
            for a given locus, therefore a search for a gene may result in
            multiple hits for the same gene name.
          </div>
        </div>
      </div>
      <div class="d-md-none">
        <div class="row">
          <div class="col-4 font-weight-bold pl-2">Search Result</div>
          <div class="col-8">
            <b-dropdown id="dropdown-1" text="Menu" class="m-md-2">
              <b-dropdown-item>New Search</b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>
    </div>
    <div class="pageMain m-2">
      <div class="d-flex flex-column">
        <div class="row mt-2">
          <div class="col-sm d-flex align-items-start ml-4">
            <button
              class="btn btn-secondary text-white btn-sm mr-2"
              @click="onReset"
              aria-label="Reset search form"
            >
              Reset
            </button>
            <button
              class="btn btn-primary text-white btn-sm"
              @click="onSubmit"
              aria-label="Submit gene search query"
            >
              Submit Query
            </button>
          </div>
        </div>
        <div>
          <div class="d-flex flex-column mx-4 my-2">
            <div v-for="(f, i) in search_form.filters" :key="i">
              <fieldset class="filter-group">
                <legend class="filter-legend">
                  {{ f.type }}
                  <a
                    v-if="f.helpLink"
                    :href="f.helpLink"
                    target="_blank"
                    style="color: black"
                    :aria-label="`Help for ${f.type}`"
                  >
                    <b-icon
                      class="ml-2"
                      :id="f.id"
                      icon="question-circle-fill"
                      aria-hidden="true"
                    ></b-icon>
                  </a>
                </legend>
                <div class="ml-4 mt-2">
                  <div v-for="(row, j) in f.rows" :key="j" class="row">
                    <div class="col-sm-4 d-flex">
                      <fieldset v-if="row.name">
                        <legend
                          class="form-label text-left fw-bold mb-1 p-0 border-0"
                          :id="'label-' + i + '-' + j"
                        >
                          {{ row.name }}
                          <Tooltip
                            v-if="row.helpTxt"
                            :help-text="row.helpTxt"
                            :help-id="row.id"
                            :label="row.name"
                          />
                        </legend>
                      </fieldset>
                    </div>
                    <div class="col-sm-8">
                      <!-- Help text for field instructions -->
                      <div
                        v-if="row.helpText"
                        :id="'help-text-' + i + '-' + j"
                        class="form-text text-black mb-2 help-text-left"
                      >
                        {{ row.helpText }}
                      </div>
                      <div
                        v-if="row.value && row.value.type == 'select'"
                        class="responsive-width mb-4"
                      >
                        <label
                          :for="'form-control-' + i + '-' + j"
                          class="sr-only"
                          >{{ row.name }}</label
                        >
                        <b-form-select
                          :id="'form-control-' + i + '-' + j"
                          class="mb-2 mr-sm-2 mb-sm-0"
                          :options="row.value.options"
                          :value="row.value.selected"
                          v-model="row.value.selected"
                          autocomplete="off"
                        ></b-form-select>
                      </div>
                      <div
                        v-else-if="row.value && row.value.type == 'input'"
                        class="responsive-width mb-4"
                      >
                        <fieldset>
                          <legend class="sr-only">{{ row.name }}</legend>
                          <b-form-input
                            :id="'form-control-' + i + '-' + j"
                            class="mb-2 mr-sm-2 mb-sm-0"
                            placeholder=""
                            autocomplete="off"
                          ></b-form-input>
                        </fieldset>
                      </div>
                      <div
                        v-else-if="row.value && row.value.type == 'multiInput'"
                        class="responsive-width mb-4"
                      >
                        <label
                          :for="'form-control-' + i + '-' + j"
                          class="sr-only"
                          >{{ row.name }}</label
                        >
                        <b-form-textarea
                          :id="'form-control-' + i + '-' + j"
                          class="mb-2 mr-sm-2 mb-sm-0"
                          placeholder=""
                          rows="3"
                          autocomplete="off"
                        ></b-form-textarea>
                      </div>
                      <div
                        v-else-if="row.value && row.value.type == 'multiSelect'"
                        class="responsive-width mb-4"
                      >
                        <label
                          :for="'form-control-' + i + '-' + j"
                          class="sr-only"
                          >{{ row.name }}</label
                        >

                        <b-form-select
                          v-if="row.value"
                          :id="'form-control-' + i + '-' + j"
                          class="mb-2 mr-sm-2 mb-sm-0"
                          :options="row.value.options"
                          :value="row.value.selected"
                          v-model="row.value.selected"
                          :select-size="4"
                          multiple
                          autocomplete="off"
                          @keyup.enter="onSubmit"
                        ></b-form-select>
                      </div>
                      <div
                        v-else-if="row.value && row.value.type == 'checkbox'"
                        class="text-left responsive-width mb-4"
                      >
                        <b-form-checkbox
                          :id="'checkbox-' + j"
                          :name="'checkbox-' + j"
                          v-model="row.value.checked"
                          autocomplete="off"
                        >
                          {{ row.value.title }}
                        </b-form-checkbox>
                      </div>
                      <div
                        v-else-if="row.value && row.value.type == 'mixed'"
                        class="text-left mb-4 d-flex flex-row w-100 align-items-center"
                      >
                        <fieldset class="mixed-controls-group">
                          <legend class="sr-only">{{ row.name }}</legend>
                          <div v-for="(e, k) in row.value.elements" :key="k">
                            <div class="d-flex ml-2">
                              <div v-if="e.type == 'select'">
                                <label
                                  :id="'label-' + i + '-' + j + '-' + k"
                                  :for="'form-control-' + i + '-' + j + '-' + k"
                                  class="sr-only"
                                >
                                  {{ row.name }}
                                  {{ e.id ? e.id.replace("-", " ") : "option" }}
                                </label>
                                <b-form-select
                                  :id="'form-control-' + i + '-' + j + '-' + k"
                                  class="mb-2 mr-sm-2 mb-sm-0"
                                  :options="e.options"
                                  :value="e.default"
                                  v-model="e.selected"
                                  autocomplete="off"
                                  @keyup.enter="onSubmit"
                                ></b-form-select>
                              </div>
                              <div v-else-if="e.type == 'input'">
                                <label
                                  :id="'label-' + i + '-' + j + '-' + k"
                                  :for="'form-control-' + i + '-' + j + '-' + k"
                                  class="sr-only"
                                >
                                  {{ e.ariaLabel || row.name + " search text" }}
                                </label>
                                <b-form-input
                                  :id="'form-control-' + i + '-' + j + '-' + k"
                                  class="mb-2 mr-sm-2 mb-sm-0"
                                  :placeholder="
                                    e.id === 'mapRange1' || e.id === 'mapRange2'
                                      ? 'Enter position'
                                      : 'Enter search term'
                                  "
                                  :value="e.default"
                                  v-model="e.value"
                                  autocomplete="off"
                                  @keyup.enter="onSubmit"
                                ></b-form-input>
                              </div>
                              <div v-else-if="e.type == 'multiInput'">
                                <label
                                  :id="'label-' + i + '-' + j + '-' + k"
                                  :for="'form-control-' + i + '-' + j + '-' + k"
                                  class="sr-only"
                                >
                                  Gene list input: {{ row.name }}
                                </label>
                                <b-form-textarea
                                  :id="'form-control-' + i + '-' + j + '-' + k"
                                  class="mb-2 mr-sm-2 mb-sm-0"
                                  ref="textarea"
                                  placeholder="Enter gene IDs, one per line (e.g., AT1G01010)"
                                  rows="3"
                                  v-model="textareaContent"
                                  autocomplete="off"
                                ></b-form-textarea>
                                <input
                                  type="file"
                                  id="fileInput"
                                  @change="handleFileUpload"
                                  style="display: none"
                                  aria-label="Upload gene list file"
                                />
                                <b-button
                                  size="sm"
                                  @click="triggerFileInputClick"
                                  class="mt-2"
                                  aria-label="Upload gene list"
                                >
                                  Upload
                                </b-button>
                              </div>
                              <div
                                class="d-flex"
                                v-else-if="e.type == 'checkbox'"
                              >
                                <label
                                  :id="'label-checkbox-' + j + '-' + k"
                                  :for="'checkbox-' + j + '-' + k"
                                  class="sr-only"
                                >
                                  {{ e.title ? e.title : "Option" }}
                                </label>
                                <b-form-checkbox
                                  :id="'checkbox-' + j + '-' + k"
                                  :name="'checkbox-' + j + '-' + k"
                                  v-model="e.checked"
                                  autocomplete="off"
                                  @keyup.enter="onSubmit"
                                >
                                  {{ e.title }}
                                </b-form-checkbox>
                              </div>

                              <div class="d-flex" v-else-if="e.type == 'text'">
                                <span>{{ e.value }}</span>
                              </div>
                            </div>
                          </div>
                        </fieldset>
                      </div>
                      <div v-else class="text-left"></div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div class="row mt-2 mb-4">
          <div class="col-sm d-flex align-items-start ml-4">
            <button
              class="btn btn-secondary text-white btn-sm mr-2"
              @click="onReset"
              aria-label="Reset search form"
            >
              Reset
            </button>
            <button
              class="btn btn-primary text-white btn-sm"
              @click="onSubmit"
              aria-label="Submit gene search query"
            >
              Submit Query
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from "@/components/common/Tooltip.vue";

export default {
  name: "GeneSearch",
  components: {
    Tooltip,
  },
  data() {
    return {
      textareaContent: "",
      crumb_links: [
        {
          text: "Home",
          href: "/",
        },
        {
          text: "Search",
          href: "/search/overview",
        },
        {
          text: "Genes",
          active: true,
        },
      ],
      search_form: {
        filters: [
          {
            type: "Search by Name or Phenotype",
            rows: [
              {},
              // {
              //   name: "Species",
              //   value: {
              //     type: "select",
              //     options: [
              //       "any",
              //       "Arabidopsis thaliana",
              //       "Arabidopsis lyrata",
              //     ],
              //     default: "Arabidopsis thaliana",
              //     selected: "Arabidopsis thaliana",
              //   },
              // },
              {
                name: "Search Text",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "select",
                      id: "search-type",
                      options: ["contains", "starts with", "exact"],
                      default: "contains",
                      selected: "contains",
                    },
                    {
                      type: "select",
                      id: "search-by",
                      options: [
                        "Gene Name",
                        "Description",
                        "Phenotype",
                        "Uniprot ID",
                        "Locus TAIR Object ID",
                        "Gene TAIR Object ID",
                      ],
                      default: "Gene Name",
                      selected: "Gene Name",
                    },
                    {
                      type: "input",
                      value: "",
                      default: "",
                    },
                    // {
                    //   type: "checkbox",
                    //   title: "Exact Match",
                    //   checked: false,
                    // },
                  ],
                },
              },
              {
                name: "Include obsoleted genes",
                value: {
                  type: "checkbox",
                  title: "",
                  checked: false,
                },
              },
            ],
          },
          {
            type: "Search by Gene List",
            rows: [
              {
                name: "Gene List Upload",
                helpText:
                  "Upload or type a list of AGI locus or gene IDs (separated by line return and no trailing spaces)",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "select",
                      options: ["AGI Locus IDs", "AGI Gene IDs"],
                      default: "AGI Locus IDs",
                      selected: "AGI Locus IDs",
                    },
                    {
                      type: "multiInput",
                      value: "",
                      default: "",
                    },
                  ],
                },
              },
            ],
          },
          {
            type: "Search by Associated Keyword",
            rows: [
              {
                name: "Keyword Term",
                value: {
                  type: "mixed",
                  elements: [
                    // {
                    //   type: "select",
                    //   options: ["contains"],
                    //   default: "contains"
                    // },
                    {
                      type: "input",
                      value: "",
                      default: "",
                    },
                  ],
                },
                helpTxt:
                  " Keywords in TAIR are controlled vocabulary terms used to describe a variety of characteristics of data. For example, functional annotation of genes includes association to keywords describing the molecular function, subcellular localization and biological process attributed to a gene as well as anatomical and temporal information related to its expression. The primary sources of keywords used in TAIR are the Gene Ontology Consortium and Arabidopsis anatomy and development terms developed as a collaboration between TAIR and other plant genome databases.",
                id: "keywordTerm",
              },
              // {
              //   name: "GO/PO ID (exact match only)",
              //   value: {
              //     type: "input"
              //   }
              // },
              {
                name: "Keyword Type",
                helpText:
                  "Select one or more keyword types. Hold Ctrl (PC) or Cmd (Mac) to select multiple options.",
                value: {
                  type: "multiSelect",
                  options: [
                    "Any",
                    "GO Molecular Function",
                    "GO Biological Process",
                    "GO Cellular Component",
                    "PO Anatomy",
                    "PO Developmental Stage",
                  ],
                  selected: ["Any"],
                },
              },
              {
                name: "Evidence",
                value: {
                  type: "multiSelect",
                  options: [
                    {
                      value: "inferred from experiment",
                      text: "inferred from experiment (EXP)",
                    },
                    {
                      value: "inferred from electronic annotation",
                      text: "inferred from electronic annotation (IEA)",
                    },
                    {
                      value: "inferred from expression pattern",
                      text: "inferred from expression pattern (IEP)",
                    },
                    {
                      value: "inferred from genetic interaction",
                      text: "inferred from genetic interaction (IGI)",
                    },
                    {
                      value: "inferred from mutant phenotype",
                      text: "inferred from mutant phenotype (IMP)",
                    },
                    {
                      value: "inferred from physical interaction",
                      text: "inferred from physical interaction (IPI)",
                    },
                    {
                      value: "inferred from sequence or structural similarity",
                      text: "inferred from sequence or structural similarity (ISS)",
                    },
                    {
                      value: "non-traceable author statement",
                      text: "non-traceable author statement (NAS)",
                    },
                    {
                      value: "no biological data available",
                      text: "no biological data available (ND)",
                    },
                    {
                      value: "traceable author statement",
                      text: "traceable author statement (TAS)",
                    },
                    {
                      value: "inferred by curator",
                      text: "inferred by curator (IC)",
                    },
                    {
                      value: "inferred from reviewed computational analysis",
                      text: "inferred from reviewed computational analysis (RCA)",
                    },
                    {
                      value: "Inferred from Biological aspect of Ancestor",
                      text: "Inferred from Biological aspect of Ancestor (IBA)",
                    },
                    {
                      value: "Inferred from Biological aspect of Descendant",
                      text: "Inferred from Biological aspect of Descendant (IBD)",
                    },
                    {
                      value: "Inferred from Key Residues",
                      text: "Inferred from Key Residues (IKR)",
                    },
                    {
                      value: "Inferred from Rapid Divergence",
                      text: "Inferred from Rapid Divergence (IRD)",
                    },
                    {
                      value: "inferred from sequence model",
                      text: "inferred from sequence model (ISM)",
                    },
                    {
                      value: "inferred from high throughput direct assay",
                      text: "inferred from high throughput direct assay (HDA)",
                    },
                    {
                      value: "inferred from high throughput expression pattern",
                      text: "inferred from high throughput expression pattern (HEP)",
                    },
                    {
                      value:
                        "inferred from high throughput genetic interaction",
                      text: "inferred from high throughput genetic interaction (HGI)",
                    },
                    {
                      value: "inferred from high throughput mutant phenotype",
                      text: "inferred from high throughput mutant phenotype (HMP)",
                    },
                  ],
                  selected: ["Any"],
                },
                helpTxt:
                  "A controlled vocabulary of codes used to identify the type of evidence supporting an annotation. The codes are intended to provide a quick way to ascertain the strength of the evidence. For example, the code IEA is used for annotations that are made based purely by computational methods whereas IDA means that the annotation is supported by a direct assay (experimental data). The codes can be used to rank the quality of annotations.",
                id: "evidence",
              },
            ],
          },
          {
            type: "Restrict by Features",
            rows: [
              {
                name: "Gene Model Type",
                helpText:
                  "Select one or more gene model types. Hold Ctrl (PC) or Cmd (Mac) to select multiple options.",
                value: {
                  type: "multiSelect",
                  options: [
                    "Any",
                    "pre trna",
                    "transposable element gene",
                    "protein coding",
                    "pseudogene",
                    "ribosomal rna",
                    "small nuclear rna",
                    "small nucleolar rna",
                    "miRNA",
                    "other rna",
                    "unknown",
                    "long noncoding rna",
                    "antisense long noncoding rna",
                    "antisense rna",
                    "novel transcribed region",
                    "uORF",
                    "miRNA primary transcript",
                  ],
                  selected: ["Any"],
                },
                helpTxt:
                  "This feature allows you to restrict your search to specific types of gene models (?).The default search returns any gene model type. To select mutiple gene model types after your first selection click on additional ones while holding down either the CTRL key (PCs) or the Apple key (Mac).",
                id: "geneModelType",
              },
              {
                name: "Advanced",
                helpText:
                  "Optional advanced filters. Hold Ctrl (PC) or Cmd (Mac) to select multiple options.",
                value: {
                  type: "multiSelect",
                  options: [
                    "has associated literature",
                    "genetic (unsequenced) loci only",
                  ],
                  selected: [],
                },
                selected: null,
              },
              // },
              // {
              //   name: "Time Restriction",
              //   value: {
              //     type: "checkbox",
              //     title: "only search last 2 months"
              //   }
              // }
            ],
          },
          {
            type: "Restrict by Map Locations",
            rows: [
              {
                name: "Chromosome",
                value: {
                  type: "select",
                  options: [
                    { text: "1", value: "1" },
                    { text: "2", value: "2" },
                    { text: "3", value: "3" },
                    { text: "4", value: "4" },
                    { text: "5", value: "5" },
                    { text: "chloroplast genome", value: "C" },
                    { text: "mitochondrial genome", value: "M" },
                  ],
                  selected: null,
                },
                selected: null,
                id: "chromosome",
                helpTxt:
                  "Lets you limit your search to a single chromosome. There are five nuclear chromosomes in Arabidopsis: 1, 2, 3, 4, and 5 and also the mitochondrial and chloroplast genomes.",
              },
              {
                name: "Map Type",
                value: {
                  type: "select",
                  options: [
                    {
                      value: "Col-CC.v2",
                      text: "reference assembly (TAIR12, Col-CC.v2)",
                    },
                    {
                      value: "physical",
                      text: "physical",
                    },
                    {
                      value: "classical",
                      text: "classical",
                    },
                  ],
                  selected: null,
                },
                helpTxt:
                  "Lets you search entities by their position on a particular map and is to be used with the Range parameters. Currently, you can search on only one map type at a time. To search and browse the genome down to the nucleotide level, use JBrowse.",
                id: "mapType",
              },
              {
                name: "Range",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      // type: "select",
                      // options: ["between", "around"],
                      // default: "between",
                      // selected: "between"
                    },
                    {
                      type: "input",
                      id: "mapRange1",
                      value: null,
                      default: null,
                      ariaLabel: "Range start position",
                    },
                    {
                      type: "text",
                      value: "and",
                    },
                    {
                      type: "input",
                      id: "mapRange2",
                      value: null,
                      default: null,
                      ariaLabel: "Range end position",
                    },
                    {
                      type: "select",
                      options: ["kbp"],
                      default: "kbp",
                      selected: "kbp",
                    },
                  ],
                },
                helpTxt: `Lets you specify a range search by the upper and lower bounds (when you select "Between") or a center point (when you select "Around"). The value is interpreted based on the selected range units. You can specify the range by genetic distance (cM), physical distance (kb), and by clone or by marker or gene names. When you select "Between" from the drop-down menu, your search will be within the range defined by two entities or positions on a particular map. When you select "Around" from the drop-down menu, your search will be the area +/-5 cM and/or +/- 150 kb from the specified entity or position. When you search around, the second value input and units options are disabled.`,
                id: "range",
              },
            ],
          },
        ],
      },
      helpLink:
        "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217049/Gene+Search+and+Results",
    };
  },
  watch: {
    "$route.query.key": function (id) {
      this.$forceUpdate();
    },
  },
  mounted() {},
  methods: {
    triggerFileInputClick() {
      document.getElementById("fileInput").click();
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          // Set the content of the textarea to the file contents
          document.getElementById("inline-form-textarea-name").value =
            // e.target.result;
            this.textareaContent = e.target.result;
        };
        reader.readAsText(file);
      }
    },
    async onSubmit(event) {
      event.preventDefault();
      // Helper function to get a filter by its name
      const getFilterByName = (name) =>
        this.search_form.filters.find((filter) => filter.type === name);

      // Helper function to get a row by its name within a filter
      const getRowByName = (filter, name) =>
        filter && filter.rows
          ? filter.rows.find((row) => row.name === name)
          : undefined;

      //Search Params
      const taxonName = "Arabidopsis thaliana";
      // Retrieve row details from "Search by Name or Phenotype" filter
      const searchFilter = getFilterByName("Search by Name or Phenotype");
      const searchRow = getRowByName(searchFilter, "Search Text");
      const obsoleteRow = getRowByName(searchFilter, "Include obsoleted genes");
      const searchBy =
        searchRow && searchRow.value && Array.isArray(searchRow.value.elements)
          ? (searchRow.value.elements.find((el) => el.id === "search-by") || {})
              .selected
          : undefined;
      const searchType =
        searchRow && searchRow.value && Array.isArray(searchRow.value.elements)
          ? (
              searchRow.value.elements.find((el) => el.id === "search-type") ||
              {}
            ).selected
          : undefined;
      const detailSearchText =
        searchRow && searchRow.value && Array.isArray(searchRow.value.elements)
          ? (searchRow.value.elements.find((el) => el.type === "input") || {})
              .value
          : undefined;
      const include_obsolete =
        obsoleteRow && obsoleteRow.value ? obsoleteRow.value.checked : false;

      //Retrieve row details from "Search by Gene List" filter
      const geneListFilter = getFilterByName("Search by Gene List");
      const geneListRow = getRowByName(geneListFilter, "Gene List Upload");
      const geneListUploadType =
        geneListRow &&
        geneListRow.value &&
        Array.isArray(geneListRow.value.elements)
          ? (
              geneListRow.value.elements.find((el) => el.type === "select") ||
              {}
            ).selected
          : undefined;
      let geneList = this.textareaContent || "";
      geneList = geneList.toLocaleUpperCase();
      geneList = geneList.replace(/\r/g, "");
      geneList = geneList.replace(/\n/g, ";");
      // Remove empty entries caused by trailing semicolons or newlines
      geneList = geneList
        .split(";")
        .map((g) => g.trim())
        .filter((g) => g.length > 0)
        .join(";");

      // Retrieve row details from "Search by Associated Keyword" filter
      const keywordFilter = getFilterByName("Search by Associated Keyword");
      const keywordTermRow = getRowByName(keywordFilter, "Keyword Term");
      const keywordTypeRow = getRowByName(keywordFilter, "Keyword Type");
      const evidenceRow = getRowByName(keywordFilter, "Evidence");
      const keywordTerm =
        keywordTermRow &&
        keywordTermRow.value &&
        Array.isArray(keywordTermRow.value.elements)
          ? (
              keywordTermRow.value.elements.find((el) => el.type === "input") ||
              {}
            ).value
          : undefined;
      const keywordType =
        keywordTypeRow && keywordTypeRow.value
          ? keywordTypeRow.value.selected
          : undefined;
      const evidence =
        evidenceRow && evidenceRow.value
          ? evidenceRow.value.selected
          : undefined;

      // Retrieve row details from "Restrict by Features" filter
      const featuresFilter = getFilterByName("Restrict by Features");
      const geneModelTypeRow = getRowByName(featuresFilter, "Gene Model Type");
      const advancedRow = getRowByName(featuresFilter, "Advanced");
      const geneModelType =
        geneModelTypeRow && geneModelTypeRow.value
          ? geneModelTypeRow.value.selected
          : undefined;
      const advanced =
        advancedRow && advancedRow.value
          ? advancedRow.value.selected
          : undefined;

      // Retrieve row details from "Restrict by Map Locations" filter
      const mapFilter = getFilterByName("Restrict by Map Locations");
      const chromosomeRow = getRowByName(mapFilter, "Chromosome");
      const mapTypeRow = getRowByName(mapFilter, "Map Type");
      const rangeRow = getRowByName(mapFilter, "Range");

      const chromosome =
        chromosomeRow && chromosomeRow.value
          ? chromosomeRow.value.selected
          : undefined;
      const mapType =
        mapTypeRow && mapTypeRow.value ? mapTypeRow.value.selected : undefined;
      const startPos =
        rangeRow && rangeRow.value && Array.isArray(rangeRow.value.elements)
          ? rangeRow.value.elements
              .filter((el) => el.id === "mapRange1")
              .map((el) => el.value)
          : [];
      const endPos =
        rangeRow && rangeRow.value && Array.isArray(rangeRow.value.elements)
          ? rangeRow.value.elements
              .filter((el) => el.id === "mapRange2")
              .map((el) => el.value)
          : [];
      const gaUnit =
        rangeRow && rangeRow.value && Array.isArray(rangeRow.value.elements)
          ? rangeRow.value.elements
              .filter((el) => el.type === "select")
              .map((el) => el.selected)
          : [];

      const search_params = {
        geneListUploadType,
        geneList,
        taxonName,
        searchType,
        searchBy,
        detailSearchText,
        include_obsolete,
        keywordTerm,
        keywordType,
        evidence,
        geneModelType,
        advanced,
        chromosome,
        mapType,
        startPos,
        endPos,
        gaUnit,
        lociMatchCount: 0,
        geneModelCount: 0,
      };

      // Call the function to show results
      // console.log(search_params);
      this.showResults(search_params);
    },
    onReset(event) {
      event.preventDefault();

      // Reset the form data to initial values
      this.textareaContent = "";

      // Reset all form fields to their default values
      this.search_form.filters.forEach((filter) => {
        filter.rows.forEach((row) => {
          if (row.value) {
            if (row.value.type === "checkbox") {
              row.value.checked = false;
            } else if (row.value.type === "select") {
              row.value.selected = row.value.default || null;
            } else if (row.value.type === "multiSelect") {
              row.value.selected =
                row.value.selected.length > 0 ? [row.value.selected[0]] : [];
            } else if (row.value.type === "mixed") {
              row.value.elements.forEach((element) => {
                if (element.type === "input") {
                  element.value = element.default || "";
                } else if (element.type === "select") {
                  element.selected = element.default || null;
                } else if (element.type === "checkbox") {
                  element.checked = false;
                } else if (element.type === "multiInput") {
                  element.value = element.default || "";
                }
              });
            }
          }
        });
      });

      // Clear any file input
      const fileInput = document.getElementById("fileInput");
      if (fileInput) {
        fileInput.value = "";
      }
    },
    showResults(inp_params) {
      let key = "searchParams_" + Date.now();
      localStorage.setItem(key, JSON.stringify(inp_params));
      this.$nextTick(() => {
        this.$router.push({
          path: "/results",
          query: {
            mainType: "detail",
            category: "genes",
            //search_params: JSON.stringify(inp_params),
            key: key,
          },
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.responsive-width {
  width: 100%;
}

@media (min-width: 768px) {
  .responsive-width {
    width: 50%;
  }
}

.locusLink {
  color: #2a568f;
  cursor: pointer;
  &:hover {
    background: #feffbb;
  }
}
.form-label {
  font-size: 18px;
  color: black;
  font-weight: bold;
}
.keySearchColor {
  color: #ff0000;
}

// Accessibility fieldset styles
.filter-group {
  border: none;
  margin: 0;
  padding: 0;
  margin-bottom: 1rem;
}

.filter-legend {
  font-size: 1.25rem;
  font-weight: bold;
  color: black;
  margin-bottom: 0.5rem;
  padding: 0;
  border: none;
  width: auto;
  display: flex;
  align-items: center;
}

// Screen reader only legend for individual controls
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// Mixed controls group styling
.mixed-controls-group {
  border: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
}

// Individual control fieldsets
fieldset:not(.filter-group):not(.mixed-controls-group) {
  border: none;
  margin: 0;
  padding: 0;
}

.help-text-left {
  text-align: left;
}
</style>
